import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { urlToService } from "@mirage/shared/util/urlRulesUtil";
import type { DocumentInfo } from "../types";

export const convertChatSourceToDocumentInfo = (
  chatSource: ChatSource,
): DocumentInfo => {
  const documentInfo: DocumentInfo = {
    uuid: "",
    url: "",
    title: "",
    lastUpdatedMs: 0,
    brandedType: "",
    body: "",
    ownerId: "",
    thirdPartyId: "",
    relevanceScore: 0,
    index: "",
  };
  switch (chatSource.type) {
    case "dash_search_result": {
      const { searchResult } = chatSource;
      documentInfo.uuid = searchResult.uuid ?? "";
      documentInfo.url = searchResult.url ?? "";
      documentInfo.title = searchResult.title ?? "";
      documentInfo.lastUpdatedMs = searchResult.updated_at_ms ?? 0;
      documentInfo.brandedType = searchResult.branded_type ?? "";
      documentInfo.body = searchResult.body ?? "";
      documentInfo.relevanceScore = searchResult.relevance_score ?? 0;
      if (searchResult.connector_info) {
        documentInfo.connectorInfo = {
          connectorId: searchResult.connector_info.connector_id ?? "",
          connectorType: 0,
          displayName: searchResult.connector_info.display_name ?? "",
          platform: 0,
          connectorName: searchResult.connector_info.connector_name ?? "",
          connectorIconUrl:
            searchResult.connector_info.connector_icon_url ?? "",
        };
        if (searchResult.connector_info.icon) {
          documentInfo.connectorInfo.icon = {
            lightUrl: searchResult.connector_info.icon.light_url ?? "",
            darkUrl: searchResult.connector_info.icon.dark_url ?? "",
            lightPngUrl: searchResult.connector_info.icon.light_png_url ?? "",
            darkPngUrl: searchResult.connector_info.icon.dark_png_url ?? "",
          };
        }
      }
      if (searchResult.file_type_info) {
        documentInfo.fileTypeInfo = {
          id: searchResult.file_type_info.id ?? "",
          displayName: searchResult.file_type_info.display_name ?? "",
        };
        if (searchResult.file_type_info.icon) {
          documentInfo.fileTypeInfo.icon = {
            lightUrl: searchResult.file_type_info.icon.light_url ?? "",
            darkUrl: searchResult.file_type_info.icon.dark_url ?? "",
            lightPngUrl: searchResult.file_type_info.icon.light_png_url ?? "",
            darkPngUrl: searchResult.file_type_info.icon.dark_png_url ?? "",
          };
        }
      }
      if (chatSource.index) {
        documentInfo.index = chatSource.index;
      }
      break;
    }
    case "recommendation": {
      const { recommendation } = chatSource;
      documentInfo.uuid = recommendation.uuid;
      documentInfo.url = recommendation.url ?? "";
      documentInfo.title = recommendation.title;
      if (recommendation.lastBrowserViewMs) {
        documentInfo.lastUpdatedMs = recommendation.lastBrowserViewMs;
      } else if (recommendation.providerUpdateAtMs) {
        documentInfo.lastUpdatedMs = recommendation.providerUpdateAtMs;
      } else {
        documentInfo.lastUpdatedMs = 0;
      }
      if (recommendation.brandedSiteInfo) {
        documentInfo.brandedType = recommendation.brandedSiteInfo.name ?? "";
      }
      if (recommendation.connectorInfo) {
        documentInfo.connectorInfo = {
          connectorId: recommendation.connectorInfo.connectorId ?? "",
          connectorType: 0, // Not directly mappable from the Recommendation type
          displayName: recommendation.connectorInfo.displayName ?? "",
          platform: 0, // Not directly mappable from the Recommendation type
          connectorName: recommendation.connectorInfo.connectorName ?? "",
          connectorIconUrl: recommendation.connectorInfo.connectorIconUrl ?? "",
        };
        if (recommendation.connectorInfo.icon) {
          documentInfo.connectorInfo.icon = {
            lightUrl: recommendation.connectorInfo.icon.lightUrl ?? "",
            darkUrl: recommendation.connectorInfo.icon.darkUrl ?? "",
            lightPngUrl: "",
            darkPngUrl: "",
          };
        }
      }
      if (recommendation.fileTypeInfo) {
        documentInfo.fileTypeInfo = {
          id: recommendation.fileTypeInfo.id ?? "",
          displayName: recommendation.fileTypeInfo.displayName ?? "",
        };
        if (recommendation.fileTypeInfo.icon) {
          documentInfo.fileTypeInfo.icon = {
            lightUrl: recommendation.fileTypeInfo.icon.lightUrl ?? "",
            darkUrl: recommendation.fileTypeInfo.icon.darkUrl ?? "",
            lightPngUrl: "",
            darkPngUrl: "",
          };
        }
      }
      if (recommendation.mimeType) {
        documentInfo.body = recommendation.mimeType;
      }
      if (recommendation.referenceId) {
        documentInfo.thirdPartyId = recommendation.referenceId;
      }
      if ("score" in recommendation) {
        documentInfo.relevanceScore = Number(recommendation.score) || 0;
      } else {
        documentInfo.relevanceScore = 0;
      }
      if (recommendation.creator?.email) {
        documentInfo.ownerId = recommendation.creator.email;
      } else if (recommendation.sender?.email) {
        documentInfo.ownerId = recommendation.sender.email;
      }
      if (chatSource.index) {
        documentInfo.index = chatSource.index;
      }
      break;
    }
    case "transient": {
      documentInfo.uuid = chatSource.uuid;
      documentInfo.url = chatSource.url;
      documentInfo.title = chatSource.title;
      documentInfo.transient = true;
      break;
    }
    case "local_file_content": {
      documentInfo.uuid = chatSource.uuid;
      documentInfo.title = chatSource.fileName;
      documentInfo.body = chatSource.content;
      documentInfo.transient = true;
      break;
    }
    case "mirage_search_result": {
      const { searchResult } = chatSource;
      const snippets = searchResult.highlights?.body?.text || [];
      documentInfo.url = searchResult.url ?? "";
      documentInfo.title = searchResult.title ?? "";
      documentInfo.lastUpdatedMs = searchResult.providerUpdateAtMs ?? 0;
      documentInfo.brandedType = searchResult.brandedType ?? "";
      documentInfo.body = snippets.length > 0 ? snippets[0] : "";
      documentInfo.relevanceScore = searchResult.relevanceScore;
      documentInfo.uuid = searchResult.uuid;
      documentInfo.connectorInfo = {
        ...searchResult.connectorInfo,
        connectorType: 0,
        platform: 0,
        icon: searchResult.connectorInfo.icon
          ? {
              lightUrl: searchResult.connectorInfo.icon.lightUrl ?? "",
              darkUrl: searchResult.connectorInfo.icon.darkUrl ?? "",
              lightPngUrl: "",
              darkPngUrl: "",
            }
          : undefined,
      };
      if (searchResult.fileTypeInfo) {
        documentInfo.fileTypeInfo = {
          id: searchResult.fileTypeInfo.id ?? "",
          displayName: searchResult.fileTypeInfo.displayName ?? "",
          icon: {
            lightUrl: searchResult.fileTypeInfo.icon?.lightUrl ?? "",
            darkUrl: searchResult.fileTypeInfo.icon?.darkUrl ?? "",
            lightPngUrl: "",
            darkPngUrl: "",
          },
        };
      }

      if (chatSource.index) {
        documentInfo.index = chatSource.index;
      }
      break;
    }
    case "stack_item": {
      const { stackItem } = chatSource;
      documentInfo.url = stackItem.url ?? "";
      documentInfo.title = stackItem.name ?? "";
      documentInfo.lastUpdatedMs = stackItem.last_modified_time_utc_sec ?? 0;
      documentInfo.brandedType = stackItem.branded_type ?? "";
      documentInfo.uuid = stackItem.uuid ?? "";
      if (chatSource.index) {
        documentInfo.index = chatSource.index;
      }
      if (stackItem.faviconUrl) {
        documentInfo.connectorInfo = {
          connectorId: urlToService(stackItem.url ?? ""),
          connectorType: 0,
          displayName: urlToService(stackItem.url ?? ""),
          platform: 0,
          connectorName: urlToService(stackItem.url ?? ""),
          connectorIconUrl: stackItem.faviconUrl,
          icon: {
            lightUrl: stackItem.faviconUrl,
            darkUrl: stackItem.faviconUrl,
            lightPngUrl: "",
            darkPngUrl: "",
          },
        };
      }
      break;
    }
    case "summarizable_result": {
      const { summarizableResult } = chatSource;
      documentInfo.url = summarizableResult.url ?? "";
      documentInfo.title =
        typeof summarizableResult.title === "string"
          ? summarizableResult.title
          : "";
      documentInfo.uuid = summarizableResult.uuid;
      if (summarizableResult.connectorInfo) {
        documentInfo.connectorInfo = {
          ...summarizableResult.connectorInfo,
          connectorType: 0,
          platform: 0,
          icon: summarizableResult.connectorInfo.icon
            ? {
                lightUrl: summarizableResult.connectorInfo.icon.lightUrl ?? "",
                darkUrl: summarizableResult.connectorInfo.icon.darkUrl ?? "",
                lightPngUrl: "",
                darkPngUrl: "",
              }
            : undefined,
        };
      }
      if (summarizableResult.fileTypeInfo) {
        documentInfo.fileTypeInfo = {
          id: summarizableResult.fileTypeInfo.id ?? "",
          displayName: summarizableResult.fileTypeInfo.displayName ?? "",
          icon: {
            lightUrl: summarizableResult.fileTypeInfo.icon?.lightUrl ?? "",
            darkUrl: summarizableResult.fileTypeInfo.icon?.darkUrl ?? "",
            lightPngUrl: "",
            darkPngUrl: "",
          },
        };
      }

      if (chatSource.index) {
        documentInfo.index = chatSource.index;
      }
    }
  }
  return documentInfo;
};
